import {announce} from '@github-ui/aria-live'
import {type RefObject, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useSubmitSuggestedChangesMutation} from '@github-ui/pull-requests/use-submit-suggested-changes-mutation'
import {useOpenManageBatchPanel} from '@github-ui/pull-requests/use-open-manage-batch-panel'
import {useIsUnifiedBatchEnabled} from '@github-ui/pull-requests/hooks/use-unified-batch-enabled'
import {buildCommentSnapshot, type CommentSnapshotInput} from '@github-ui/pull-requests/helpers/comment-snapshot'
import {Button} from '@primer/react'

import {useSetIsDialogOpen} from '../../contexts/DialogStateContext'

import type {SuggestedChange} from '../../conversation-types'
import {ApplyOrAddToBatch} from './CodeSuggestionActions/ApplyOrAddToBatch'
import {ApplySuggestionDialog} from './CodeSuggestionActions/ApplySuggestionDialog'
import {CodeSuggestionUnavailable} from './CodeSuggestionActions/CodeSuggestionUnavailable'
import {SUGGESTED_CHANGES_ERRORS, validateSuggestedChangeCanBeAddedToBatch} from '../../helpers/suggested-changes'
import {
  useComparisonInfo,
  useSuggestionBatch,
  useIsInUnifiedBatch,
  useThreadHasBatchedSuggestion,
  useUnifiedBatchActions,
} from '@github-ui/pull-requests/pull-request-store-selectors'
import {
  UnifiedCommentActionTelemetryEvents,
  UnifiedCommentActionTelemetryPlacements,
  useUnifiedCommentActionTelemetry,
} from './unified-comment-action-telemetry'

export function CodeSuggestionActions({
  suggestedChange,
  isAutomatedComment,
  comment,
}: {
  suggestedChange: SuggestedChange
  isAutomatedComment?: boolean
  comment: CommentSnapshotInput
}) {
  const isUnifiedBatchEnabled = useIsUnifiedBatchEnabled()

  if (isUnifiedBatchEnabled) {
    return (
      <CommitSuggestionActions
        suggestedChange={suggestedChange}
        isAutomatedComment={isAutomatedComment}
        comment={comment}
      />
    )
  }

  return <LegacyCodeSuggestionActions suggestedChange={suggestedChange} />
}

function LegacyCodeSuggestionActions({suggestedChange}: {suggestedChange: SuggestedChange}) {
  const setIsDialogOpen = useSetIsDialogOpen()
  const [isApplySuggestionsDialogOpen, setIsApplySuggestionsDialogOpen] = useState(false)
  const returnFocusRef = useRef<RefObject<HTMLElement | null> | undefined>(undefined)
  const suggestionBatch = useSuggestionBatch()

  const isPendingInBatch =
    suggestedChange.commentDatabaseId !== undefined &&
    suggestedChange.commentDatabaseId !== null &&
    suggestionBatch.activeBatch.some(
      id =>
        id.commentDatabaseId === suggestedChange.commentDatabaseId &&
        id.suggestionIndex === suggestedChange.suggestionIndex,
    )

  // Validate using the batch which now stores threadId and pathLineRanges
  const {isValid, reason} = validateSuggestedChangeCanBeAddedToBatch(
    {
      threadId: suggestedChange.threadId,
      pathLineRanges: [{path: suggestedChange.path, lineRange: suggestedChange.lineRange}],
    },
    suggestionBatch.activeBatch,
  )

  const changesToApply = useMemo(() => [suggestedChange], [suggestedChange])

  const authorLogins = changesToApply.map((change: SuggestedChange) => change.authorLogin)

  const setDialogOpen = useCallback(
    (isDialogOpen: boolean) => {
      setIsApplySuggestionsDialogOpen(isDialogOpen)
      setIsDialogOpen?.(isDialogOpen)
    },
    [setIsDialogOpen],
  )

  const closeDialog = useCallback(() => {
    setDialogOpen(false)

    // manually return focus to the button that opened the dialog
    if (returnFocusRef.current) {
      const ref = returnFocusRef.current.current
      setTimeout(() => ref?.focus())
    }
  }, [setDialogOpen])

  const openDialog = useCallback(
    (returnFocusTo?: RefObject<HTMLElement | null>) => {
      setDialogOpen(true)

      // store a ref to an element we will return focus to when the dialog closes
      returnFocusRef.current = returnFocusTo
    },
    [setDialogOpen, returnFocusRef],
  )

  const {mutate: submitSuggestions} = useSubmitSuggestedChangesMutation()
  const {
    fullDiff: {headOid},
  } = useComparisonInfo()
  const handleCommit = useCallback(
    (
      commitMessage: string,
      onError: (error: Error, type?: string, friendlyMessage?: string) => void,
      onCompleted: () => void,
    ) => {
      submitSuggestions(
        {changes: changesToApply, message: commitMessage, currentOid: headOid},
        {
          onSuccess: () => {
            closeDialog()
            onCompleted()
          },
          onError: (error: Error) => {
            onError(error, 'submitSuggestedChanges', 'Failed to submit suggested changes')
          },
        },
      )
    },
    [changesToApply, closeDialog, headOid, submitSuggestions],
  )

  return (
    <div className="border-top p-2">
      <ApplyOrAddToBatch suggestedChange={suggestedChange} onOpenDialog={openDialog} canAddToBatch={isValid} />

      {!isPendingInBatch && !isValid && reason && <CodeSuggestionUnavailable reason={reason} className="m-0" />}

      {isApplySuggestionsDialogOpen && (
        <ApplySuggestionDialog
          authorLogins={authorLogins}
          batchSize={changesToApply.length}
          commentKind="human"
          onClose={closeDialog}
          onCommit={handleCommit}
        />
      )}
    </div>
  )
}

function CommitSuggestionActions({
  suggestedChange,
  isAutomatedComment,
  comment,
}: {
  suggestedChange: SuggestedChange
  isAutomatedComment?: boolean
  comment: CommentSnapshotInput
}) {
  const setIsDialogOpen = useSetIsDialogOpen()
  const [isApplySuggestionsDialogOpen, setIsApplySuggestionsDialogOpen] = useState(false)
  const returnFocusRef = useRef<HTMLButtonElement>(null)
  const changesToApply = useMemo(() => [suggestedChange], [suggestedChange])
  const authorLogins = changesToApply.map((change: SuggestedChange) => change.authorLogin)
  const sendTelemetry = useUnifiedCommentActionTelemetry(
    {
      hasSuggestion: true,
      isOutdated: false,
      kind: isAutomatedComment ? 'ccr-with-suggestion' : 'human-with-suggestion',
      suggestionIndex: isAutomatedComment ? undefined : suggestedChange.suggestionIndex,
    },
    {
      controlPlacement: UnifiedCommentActionTelemetryPlacements.perSuggestionControls,
    },
  )

  const setDialogOpen = useCallback(
    (isDialogOpen: boolean) => {
      setIsApplySuggestionsDialogOpen(isDialogOpen)
      setIsDialogOpen?.(isDialogOpen)
    },
    [setIsDialogOpen],
  )

  const closeDialog = useCallback(() => {
    setDialogOpen(false)
    setTimeout(() => returnFocusRef.current?.focus())
  }, [setDialogOpen])
  const openDialog = useCallback(() => {
    sendTelemetry(UnifiedCommentActionTelemetryEvents.commitSuggestionsClicked)
    setDialogOpen(true)
  }, [sendTelemetry, setDialogOpen])

  const {mutate: submitSuggestions} = useSubmitSuggestedChangesMutation()
  const {
    fullDiff: {headOid},
  } = useComparisonInfo()
  const handleCommit = useCallback(
    (
      commitMessage: string,
      onError: (error: Error, type?: string, friendlyMessage?: string) => void,
      onCompleted: () => void,
    ) => {
      submitSuggestions(
        {changes: changesToApply, message: commitMessage, currentOid: headOid},
        {
          onSuccess: () => {
            closeDialog()
            onCompleted()
          },
          onError: (error: Error) => {
            onError(error, 'submitSuggestedChanges', 'Failed to submit suggested changes')
          },
        },
      )
    },
    [changesToApply, closeDialog, headOid, submitSuggestions],
  )

  const suggestionActions =
    !isAutomatedComment && suggestedChange.commentDatabaseId != null ? (
      <SuggestionBatchControls
        suggestedChange={suggestedChange}
        commentDatabaseId={suggestedChange.commentDatabaseId}
        comment={comment}
        onOpenCommitDialog={openDialog}
        returnFocusRef={returnFocusRef}
      />
    ) : null

  return (
    <div className="border-top p-2">
      {suggestionActions ?? (
        <div className="d-flex flex-row flex-items-center gap-2 flex-justify-end">
          <Button variant="primary" onClick={openDialog} ref={returnFocusRef}>
            Commit suggestions
          </Button>
        </div>
      )}

      {isApplySuggestionsDialogOpen && (
        <ApplySuggestionDialog
          authorLogins={authorLogins}
          batchSize={changesToApply.length}
          commentKind="human"
          dialogTitle="Commit suggestions"
          onClose={closeDialog}
          onCommit={handleCommit}
          submitButtonLabel="Commit suggestions"
        />
      )}
    </div>
  )
}

function SuggestionBatchControls({
  suggestedChange,
  commentDatabaseId,
  comment,
  onOpenCommitDialog,
  returnFocusRef,
}: {
  suggestedChange: SuggestedChange
  commentDatabaseId: number
  comment: CommentSnapshotInput
  onOpenCommitDialog: () => void
  returnFocusRef: RefObject<HTMLButtonElement | null>
}) {
  const batchId = useMemo(
    () => ({
      commentDatabaseId,
      suggestionIndex: suggestedChange.suggestionIndex,
    }),
    [commentDatabaseId, suggestedChange.suggestionIndex],
  )
  const isInBatch = useIsInUnifiedBatch(batchId)
  const {addItem, removeItem} = useUnifiedBatchActions()
  const openManageBatchPanel = useOpenManageBatchPanel('per_comment_add_to_batch')
  const addToBatchButtonRef = useRef<HTMLButtonElement>(null)
  const threadHasBatchedSuggestion = useThreadHasBatchedSuggestion(suggestedChange.threadId)
  // Another suggestion from the same thread is already in the batch
  const threadHasOtherBatchedSuggestion = !isInBatch && threadHasBatchedSuggestion

  const prevThreadHasOtherBatchedSuggestionRef = useRef(threadHasOtherBatchedSuggestion)
  useEffect(() => {
    if (threadHasOtherBatchedSuggestion && !prevThreadHasOtherBatchedSuggestionRef.current) {
      announce(SUGGESTED_CHANGES_ERRORS.INVALID_RANGE)
    }
    prevThreadHasOtherBatchedSuggestionRef.current = threadHasOtherBatchedSuggestion
  }, [threadHasOtherBatchedSuggestion])

  // Snapshot extraction parses and re-serializes the comment body (`DOMParser`), so we keep it
  // out of render and build the full item lazily in `handleAddToBatch`. Telemetry only reads
  // the cheap identity fields below, never the snapshot.
  const batchItemBase = useMemo(
    () => ({
      commentGlobalId: suggestedChange.commentId,
      commentDatabaseId,
      path: suggestedChange.path,
      isOutdated: false,
      threadId: suggestedChange.threadId ?? null,
      hasSuggestion: true as const,
      lineRange: suggestedChange.lineRange,
      kind: 'human-with-suggestion' as const,
      suggestionIndex: suggestedChange.suggestionIndex,
    }),
    [
      suggestedChange.commentId,
      commentDatabaseId,
      suggestedChange.lineRange,
      suggestedChange.path,
      suggestedChange.suggestionIndex,
      suggestedChange.threadId,
    ],
  )
  const sendTelemetry = useUnifiedCommentActionTelemetry(batchItemBase, {
    controlPlacement: UnifiedCommentActionTelemetryPlacements.perSuggestionControls,
  })

  const [addToBatchError, setAddToBatchError] = useState<string | undefined>()

  const handleAddToBatch = useCallback(() => {
    sendTelemetry(UnifiedCommentActionTelemetryEvents.addToBatchClicked)
    const result = addItem({
      ...batchItemBase,
      ...buildCommentSnapshot(comment, {suggestionIndex: suggestedChange.suggestionIndex}),
    })
    if (!result.ok) {
      setAddToBatchError(result.reason)
      announce(result.reason)
    } else {
      setAddToBatchError(undefined)
      openManageBatchPanel({returnFocusTo: addToBatchButtonRef.current})
    }
  }, [addItem, batchItemBase, comment, suggestedChange.suggestionIndex, openManageBatchPanel, sendTelemetry])

  const handleRemoveFromBatch = useCallback(() => {
    sendTelemetry(UnifiedCommentActionTelemetryEvents.removeFromBatchClicked)
    removeItem(batchId)
    setAddToBatchError(undefined)
  }, [removeItem, batchId, sendTelemetry])

  return (
    <>
      {threadHasOtherBatchedSuggestion ? (
        <CodeSuggestionUnavailable reason={SUGGESTED_CHANGES_ERRORS.INVALID_RANGE} className="m-0" />
      ) : (
        <div className="d-flex flex-row flex-items-center gap-2 flex-justify-end">
          <Button
            variant={isInBatch ? 'danger' : 'default'}
            onClick={isInBatch ? handleRemoveFromBatch : handleAddToBatch}
            ref={addToBatchButtonRef}
          >
            {isInBatch ? 'Remove from batch' : 'Add to batch'}
          </Button>
          {!isInBatch && (
            <Button variant="primary" onClick={onOpenCommitDialog} ref={returnFocusRef}>
              Commit suggestions
            </Button>
          )}
        </div>
      )}

      {addToBatchError && !threadHasOtherBatchedSuggestion && (
        <CodeSuggestionUnavailable reason={addToBatchError} className="m-0" />
      )}
    </>
  )
}
